import {
    SpaceBetween,
    Header,
    Box,
    Table,
    Button,
    Pagination,
    Flashbar,
} from '@amzn/awsui-components-console';
import React, { useState, useMemo } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useHistory } from 'react-router-dom';
import { ALARM_RULE_COLUMNS, UI_STRINGS } from './alarmRuleConstants';
import { configureLab } from '../apis';
import DeleteAlarmRuleModal from './DeleteAlarmRuleModal';
import { displayErrorContent } from '../../../lib/utils';
import { SERVER_ERROR } from '../../../lib/constants';

export default function AlarmRulesTable({ labId, alarmRules, labData, onRulesChange }) {
    const history = useHistory();
    const rules = alarmRules || [];
    const [selectedItems, setSelectedItems] = useState([]);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState([]);

    // Memoize rules with index to maintain stable references for selection tracking
    const rulesWithIndex = useMemo(
        () => rules.map((rule, index) => ({ ...rule, _index: index })),
        [alarmRules]
    );

    const { items, collectionProps, paginationProps } = useCollection(rulesWithIndex, {
        pagination: { pageSize: 10 },
        selection: {},
    });

    const selectedRule = selectedItems[0];
    const selectedRuleIndex = selectedRule ? selectedRule._index : -1;

    const onAddRuleClick = () => {
        history.push({
            pathname: `/lab-management/labs/configure/${labId}/alarm-rules`,
            state: { labId, alarmRules: rules, labData },
        });
    };

    const onEditClick = () => {
        if (!selectedRule || selectedRuleIndex < 0) return;
        history.push({
            pathname: `/lab-management/labs/configure/${labId}/alarm-rules/${selectedRuleIndex}`,
            state: { labId, alarmRules: rules, labData, ruleIndex: selectedRuleIndex, rule: rules[selectedRuleIndex] },
        });
    };

    const onDeleteClick = () => {
        setDeleteModalVisible(true);
    };

    const onDeleteConfirm = async () => {
        if (selectedRuleIndex < 0) return;
        setDeleteLoading(true);
        setErrorMessage([]);
        try {
            const updatedRules = rules.filter((_, index) => index !== selectedRuleIndex);
            await configureLab(
                labData.labId,
                labData.owner,
                labData.accessGroup,
                labData.ticketCategory,
                labData.ticketType,
                labData.ticketItem,
                labData.ticketGroup,
                labData.dotsPoolName,
                labData.monitorEnabled,
                updatedRules
            );
            onRulesChange(updatedRules);
            setSelectedItems([]);
            setDeleteModalVisible(false);
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status === 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMsg = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMsg, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    UI_STRINGS.ERROR_DELETE,
                    setErrorMessage
                );
            }
            setDeleteModalVisible(false);
        }
        setDeleteLoading(false);
    };

    const onDeleteDismiss = () => {
        setDeleteModalVisible(false);
    };

    return (
        <SpaceBetween size="s">
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                selectedItems={selectedItems}
                selectionType="single"
                header={
                    <Header
                        counter={`(${rules.length})`}
                        description={UI_STRINGS.DESCRIPTION}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button disabled={selectedItems.length === 0} onClick={onEditClick}>
                                    {UI_STRINGS.TABLE_EDIT_BUTTON}
                                </Button>
                                <Button disabled={selectedItems.length === 0} onClick={onDeleteClick}>
                                    {UI_STRINGS.DELETE}
                                </Button>
                                <Button variant="primary" onClick={onAddRuleClick}>
                                    {UI_STRINGS.TABLE_ADD_BUTTON}
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        {UI_STRINGS.TABLE_TITLE}
                    </Header>
                }
                columnDefinitions={ALARM_RULE_COLUMNS}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box variant="strong">{UI_STRINGS.TABLE_EMPTY_TITLE}</Box>
                        <Box variant="p" color="inherit">
                            {UI_STRINGS.TABLE_EMPTY_SUBTITLE}
                        </Box>
                    </Box>
                }
                loadingText={UI_STRINGS.TABLE_LOADING}
            />
            <Flashbar items={errorMessage} />
            <DeleteAlarmRuleModal
                visible={deleteModalVisible}
                onDismiss={onDeleteDismiss}
                onConfirm={onDeleteConfirm}
                loading={deleteLoading}
            />
        </SpaceBetween>
    );
}
